import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Produto } from './api.js'

export type ItemCarrinho = {
  /** Linha do carrinho. Dois X-Tudo com adicionais diferentes são linhas distintas. */
  linhaId: string
  /**
   * Produto + adicionais + observação. É o que decide se somar quantidade ou
   * abrir linha nova.
   *
   * Campo próprio, e não um pedaço do `linhaId`: comparar por PREFIXO do
   * linhaId juntava linhas erradas. A assinatura de um item SEM observação
   * ("p1||") é prefixo da de um item COM observação ("p1||sem cebola|0"), então
   * adicionar um X-Tudo normal somava no X-Tudo "sem cebola" que já estava no
   * carrinho — a pessoa pedia um lanche comum e recebia dois sem cebola.
   *
   * Item que já está no localStorage de alguém não tem este campo; ele não casa
   * com nada e vira linha nova. Feio por uma visita, e nunca errado.
   */
  assinatura: string
  produtoId: string
  nome: string
  fotoUrl: string | null
  quantidade: number
  observacao?: string
  adicionais: { adicionalId: string; nome: string; quantidade: number }[]
}

type Carrinho = {
  itens: ItemCarrinho[]
  adicionar: (
    p: Produto,
    dados: Omit<ItemCarrinho, 'linhaId' | 'assinatura' | 'produtoId' | 'nome' | 'fotoUrl'>,
  ) => void
  alterarQuantidade: (linhaId: string, quantidade: number) => void
  remover: (linhaId: string) => void
  limpar: () => void
  totalItens: number
}

const Ctx = createContext<Carrinho | null>(null)
const CHAVE = 'du:carrinho'

export function useCarrinho() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useCarrinho precisa estar dentro de <ProvedorCarrinho>')
  return ctx
}

/**
 * O carrinho guarda O QUE foi pedido, NUNCA quanto custa.
 *
 * Preço nenhum é persistido aqui de propósito: se a loja mudar o valor do
 * X-Tudo enquanto o carrinho está aberto no celular de alguém, o total tem de
 * mudar junto. Quem decide o valor é sempre a API, pela rota de cotação.
 */
export function ProvedorCarrinho({ children }: { children: React.ReactNode }) {
  const [itens, setItens] = useState<ItemCarrinho[]>(() => {
    try {
      const salvo = localStorage.getItem(CHAVE)
      return salvo ? (JSON.parse(salvo) as ItemCarrinho[]) : []
    } catch {
      return []
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE, JSON.stringify(itens))
    } catch {
      /* navegação privada pode recusar; o carrinho segue na memória */
    }
  }, [itens])

  const adicionar = useCallback<Carrinho['adicionar']>((produto, dados) => {
    setItens((atuais) => {
      // Mesma combinação de produto + adicionais + observação soma quantidade,
      // em vez de virar duas linhas iguais na tela.
      const assinatura = `${produto.id}|${dados.adicionais
        .map((a) => `${a.adicionalId}x${a.quantidade}`)
        .sort()
        .join(',')}|${dados.observacao ?? ''}`
      const existente = atuais.find((i) => i.assinatura === assinatura)
      if (existente) {
        return atuais.map((i) =>
          i === existente ? { ...i, quantidade: Math.min(50, i.quantidade + dados.quantidade) } : i,
        )
      }
      return [
        ...atuais,
        {
          ...dados,
          linhaId: `${assinatura}|${atuais.length}`,
          assinatura,
          produtoId: produto.id,
          nome: produto.nome,
          fotoUrl: produto.fotoUrl,
        },
      ]
    })
  }, [])

  const alterarQuantidade = useCallback((linhaId: string, quantidade: number) => {
    setItens((atuais) =>
      quantidade <= 0
        ? atuais.filter((i) => i.linhaId !== linhaId)
        : atuais.map((i) => (i.linhaId === linhaId ? { ...i, quantidade } : i)),
    )
  }, [])

  const remover = useCallback((linhaId: string) => {
    setItens((atuais) => atuais.filter((i) => i.linhaId !== linhaId))
  }, [])

  const limpar = useCallback(() => setItens([]), [])

  const valor = useMemo(
    () => ({
      itens,
      adicionar,
      alterarQuantidade,
      remover,
      limpar,
      totalItens: itens.reduce((s, i) => s + i.quantidade, 0),
    }),
    [itens, adicionar, alterarQuantidade, remover, limpar],
  )

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>
}

/** Converte o carrinho no formato que a API espera. */
export function paraApi(itens: ItemCarrinho[]) {
  return itens.map((i) => ({
    produtoId: i.produtoId,
    quantidade: i.quantidade,
    ...(i.observacao ? { observacao: i.observacao } : {}),
    ...(i.adicionais.length > 0
      ? { adicionais: i.adicionais.map((a) => ({ adicionalId: a.adicionalId, quantidade: a.quantidade })) }
      : {}),
  }))
}
